import React, { createContext, useContext, useState } from 'react';
import { useDIContext } from '@/Framework/DI/DIContext';
import PortfolioMonitorRepository from '@/finsight/infrastructure/repository/PortfolioMonitorRepository';
import { AlertManager } from '@dealroadshow/uikit/core/components/Alert';
import { getErrorMessage } from '@/Framework/Message/Mapper/getMessage';
import { IPortfolioItem, IPortfolioItemPure } from '@/finsight/domain/vo/PortfolioMonitor/IPortfoliosCollection';
import { usePortfoliosContext } from '@/finsight/application/PortfolioMonitor/PortfoliosContext';
import { getMergedPortfolios } from './helpers';

export const useManagePortfolios = () => {
  const portfolioMonitorRepository = useDIContext()
    .container
    .get<PortfolioMonitorRepository>(PortfolioMonitorRepository);

  const { setPortfolioCollection } = usePortfoliosContext();
  const [isDeletePortfolioFetching, setIsDeletePortfolioFetching] = useState(false);
  const [isManagePortfoliosFetching, setIsManagePortfoliosFetching] = useState(false);

  const getPortfolios = async () => {
    setIsManagePortfoliosFetching(true);
    try {
      const { collection } = await portfolioMonitorRepository.getPortfolios({
        sortBy: 'priority',
        sortOrder: 'asc',
      });

      setPortfolioCollection((prevState) => getMergedPortfolios(prevState, collection));
    } catch (e) {
      AlertManager.error(getErrorMessage(e));
    } finally {
      setIsManagePortfoliosFetching(false);
    }
  };

  const deletePortfolio = async (portfolio: IPortfolioItemPure): Promise<boolean> => {
    setIsDeletePortfolioFetching(true);
    try {
      await portfolioMonitorRepository.deletePortfolio(portfolio.id);
      AlertManager.success(`The ${ portfolio.name } portfolio has been deleted.`);
      return true;
    } catch (error) {
      AlertManager.error(getErrorMessage(error));
      return null;
    } finally {
      setIsDeletePortfolioFetching(false);
    }
  };

  const managePortfolios = async (portfolios: IPortfolioItem[]): Promise<IPortfolioItem[]> => {
    setIsManagePortfoliosFetching(true);
    try {
      const response = await portfolioMonitorRepository.managePortfolios(portfolios);
      AlertManager.success('Changes successfully saved!');
      return response;
    } catch (error) {
      AlertManager.error(getErrorMessage(error));
      return null;
    } finally {
      setIsManagePortfoliosFetching(false);
    }
  };

  return {
    isDeletePortfolioFetching,
    isManagePortfoliosFetching,
    deletePortfolio,
    managePortfolios,
    getPortfolios,
  };
};

type PortfolioMonitorContextType = ReturnType<typeof useManagePortfolios>;

export const ManagePortfoliosContext = createContext<PortfolioMonitorContextType>(null);

export const useManagePortfoliosContext = () => {
  const context = useContext(ManagePortfoliosContext);
  if (!context) {
    throw new Error('useManagePortfoliosContext must be used within the ManagePortfoliosContext');
  }
  return context;
};

interface IProps {
  children: React.ReactNode,
}

const ManagePortfoliosContextProvider = ({ children }: IProps) => (
  <ManagePortfoliosContext.Provider value={ useManagePortfolios() }>
    { children }
  </ManagePortfoliosContext.Provider>
);

export default ManagePortfoliosContextProvider;
